import React, { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import Card from '../../ui/Card';
import DataTable from '../../ui/DataTable/DataTable';
import { CellDate, CellText } from '../../ui/DataTable/cells';
import { getImporter } from '../../services/importers';
import { HISTORY_SIZE } from '../../services/importLogService';
import ImportCard, { fmtCounts } from './components/ImportCard';

/** Last imports / checks of the table, from `import_log/<table>.history` (newest first). */
function RecentImports({ importer, lastLog }) {
    const history = Array.isArray(lastLog?.history) ? lastLog.history : [];
    const columns = [
        {
            key: 'at',
            header: 'Date',
            sortValue: (h) => (h.at ? new Date(h.at).getTime() : null),
            cell: (h) => <CellDate value={h.at} time />,
        },
        { key: 'user', header: 'User', type: 'text' },
        { key: 'mode', header: 'Mode', type: 'tag' },
        {
            key: 'fileName',
            header: 'File',
            type: 'text',
        },
        {
            key: 'counts',
            header: 'Counts',
            sortable: false,
            nowrap: false,
            accessor: (h) => fmtCounts(h.counts, importer.countLabels),
            cell: (h) => <CellText max={420}>{fmtCounts(h.counts, importer.countLabels)}</CellText>,
        },
    ];
    return (
        <Card
            className="mt-4"
            title="Recent imports"
            subtitle={<>Last {HISTORY_SIZE} imports and checks of <code>{importer.table}</code>.</>}
        >
            <DataTable
                columns={columns}
                data={history}
                rowKey={(h, i) => `${h.at}-${i}`}
                searchable={false}
                pagination={false}
                columnToggle={false}
                card={false}
                emptyTitle={lastLog ? 'History starts with the next import' : 'No import recorded yet.'}
                emptyDescription={lastLog ? 'The latest entry is shown on the card above.' : undefined}
            />
        </Card>
    );
}

/** /database/:importerId - the import card of one table (full width) plus its recent imports. */
export default function ImporterPage() {
    const { importerId } = useParams();
    const [searchParams] = useSearchParams();
    const importer = getImporter(importerId);
    const [lastLog, setLastLog] = useState(null);
    // The hub card's "Undo last import" links here with ?undo=1: the confirmation opens in the card below
    const autoUndo = searchParams.get('undo') === '1';

    if (!importer) {
        return (
            <Layout title="Database" subtitle="Unknown table" backPath="/database">
                <div className="max-w-[900px] mx-auto px-2">
                    <Card>
                        <p className="text-sm text-gray-700 m-0">No importer named <code>{importerId}</code>.</p>
                        {/* old links to the removed uploads land here (owner, 28 Sep 2026: every list comes from Ejar) */}
                        <p className="text-sm text-gray-700 m-0 mt-1">Contracts, airport contracts, customer contacts, the E-Check archive, the fleet, Invygo bookings and fuel tank sizes are no longer uploaded: the engine pulls them from Ejar every hour.</p>
                        <Link to="/database" className="text-sm text-purple-700 underline">Back to the Database hub</Link>
                    </Card>
                </div>
            </Layout>
        );
    }

    return (
        <Layout title={importer.title} subtitle={`Table: ${importer.table}`} backPath="/database">
            <div className="max-w-[1200px] mx-auto px-2">
                <p className="text-xs m-0 mb-3">
                    <Link to="/database" className="text-purple-700 underline">All tables</Link>
                    <span className="text-gray-500"> / {importer.title}</span>
                </p>
                <ImportCard importer={importer} onLogChange={setLastLog} autoUndo={autoUndo} />
                <RecentImports importer={importer} lastLog={lastLog} />
            </div>
        </Layout>
    );
}
